import React from 'react';
import { Helmet } from 'react-helmet-async';
import config from '../config';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  author,
  publishDate,
  modifiedDate,
  category,
  tags,
  readingTime,
  jsonLd
}) => {
  const siteTitle = title ? `${title} | ${config.SITE_NAME}` : config.SITE_NAME;
  const siteDescription = description || config.SITE_DESCRIPTION;
  const siteUrl = url || config.SITE_URL;
  const siteImage = image || `${config.SITE_URL}/og-image.jpg`;

  // Generate structured data (JSON-LD)
  const generateStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": type === 'article' ? "BlogPosting" : "WebSite",
      "name": siteTitle,
      "description": siteDescription,
      "url": siteUrl,
      "image": siteImage,
      "publisher": {
        "@type": "Organization",
        "name": config.SITE_NAME,
        "url": config.SITE_URL,
        "logo": {
          "@type": "ImageObject",
          "url": `${config.SITE_URL}/logo.png`
        }
      }
    };

    if (type === 'article') {
      return {
        ...baseData,
        "@type": "BlogPosting",
        "headline": title,
        "author": {
          "@type": "Person",
          "name": author
        },
        "datePublished": publishDate,
        "dateModified": modifiedDate,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": siteUrl
        },
        "articleSection": category,
        "keywords": keywords,
        "wordCount": readingTime ? readingTime * 200 : 500, // Estimate words based on reading time
        "timeRequired": readingTime ? `PT${readingTime}M` : "PT5M"
      };
    }

    return baseData;
  };

  const structuredData = jsonLd || generateStructuredData();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Language and Region */}
      <html lang="en" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      <meta name="ICBM" content="40.0150, -105.2705" />

      {/* Canonical URL */}
      <link rel="canonical" href={siteUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || config.SITE_NAME} />
      <meta property="og:site_name" content={config.SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific Open Graph tags */}
      {type === 'article' && (
        <>
          <meta property="article:published_time" content={publishDate} />
          <meta property="article:modified_time" content={modifiedDate} />
          <meta property="article:author" content={author} />
          <meta property="article:section" content={category} />
          {tags && tags.split(',').map((tag, index) => (
            <meta key={index} property="article:tag" content={tag.trim()} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      <meta name="twitter:image:alt" content={title || config.SITE_NAME} />
      <meta name="twitter:site" content="@blogcraft" />
      <meta name="twitter:creator" content={author ? `@${author.replace(/\s+/g, '')}` : '@blogcraft'} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      <meta name="application-name" content={config.SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={config.SITE_NAME} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=no" />

      {/* Viewport and Rendering */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="referrer" content="no-referrer-when-downgrade" />

      {/* Content Type and Encoding */}
      <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
      <meta httpEquiv="Content-Language" content="en" />

      {/* Cache Control */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />

      {/* DNS Prefetch for Performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />

      {/* Preconnect for Critical Resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" crossOrigin="anonymous" />

      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Alternate Language Links */}
      <link rel="alternate" hrefLang="en" href={siteUrl} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />

      {/* Additional Performance */}
      <link rel="preload" href="/fonts/inter-var.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEO;
